import React, { cloneElement, isValidElement, Children } from 'react';
import { useSelector } from 'react-redux';
import { Form, DropdownItem } from 'semantic-ui-react';
import { StringUtils } from 'utils';
import { PRODUCT_PERMISSIONS, USER_PERMISSIONS } from './PERMISSIONS';
import './index.scss';

const PropWrapper = ({ children, disabled, controlledPopupDisabled, popupDisabled, readOnlyInput, readOnlyDropdown, readOnlyNestedDropdown, onHoverOnClick, link, readOnlyDropdownOption }) => {    return Children.map(children, child => {
        if (isValidElement(child) && disabled) {
            return cloneElement(child, { disabled: disabled });
        }
        if (isValidElement(child) && controlledPopupDisabled) {
            const childProp = child.props.trigger.props;
            const propObj = childProp?.text ? childProp : childProp?.trigger?.props;
            return cloneElement(
                <DropdownItem
                    text={propObj?.text}
                    icon={propObj?.icon}
                    disabled
                />);
        }
        if (isValidElement(child) && popupDisabled) {
            const childProp = child.props.trigger
            return cloneElement(childProp, { disabled: popupDisabled });
        }
        if (isValidElement(child) && readOnlyInput) {
            return cloneElement(child, { readOnly: readOnlyInput });
        }
        if (isValidElement(child) && readOnlyDropdown) {
            const childProp = child.props;
            return cloneElement(
                <Form.Input 
                    label={childProp.label} 
                    required={childProp.required} 
                    value={childProp.options.find(option => option.value === childProp.value)?.text} 
                    width={childProp.width} 
                    readOnly 
                />);
        }
        if (isValidElement(child) && readOnlyDropdownOption) {
            const childProp = child?.props;
            const nestedChild = childProp?.children?.props?.children
            return cloneElement(
                <Form.Input 
                    label={childProp.label} 
                    required={childProp.required} 
                    value={nestedChild?.find(option => option?.props?.children?.props?.value === childProp.value)?.props?.children?.props?.text} 
                    width={childProp.width} 
                    readOnly 
                />);
        }
        if (isValidElement(child) && readOnlyNestedDropdown) {
            const childProp = child.props;
            const nestedChild = childProp.nested_options.find(nestedParent => nestedParent.value === childProp.parent_value);
            return cloneElement(
                <Form.Input 
                    label={childProp.label} 
                    required={childProp.required} 
                    value={`${nestedChild?.text}/${nestedChild?.options.find(option => option.value === childProp.child_value)?.text}`} 
                    width={childProp.width} 
                    readOnly 
                />);
        }
         if (isValidElement(child) && onHoverOnClick) {
            return cloneElement(child, { onClick: () => { }});
        }
        if (isValidElement(child) && link) {
            return cloneElement(child, { className: 'disabled--link' });
        }
        return child;
    });
};

const fourZeroThree = (error403) => {
    return error403 !== undefined ? error403 : '';
}

const AccessWrapper = ({type, children, fourZeroThree}) => {
    switch(type){
        case 'disabled':
            return <PropWrapper disabled>{children}</PropWrapper>;
        case 'controlledPopupDisabled':
            return <PropWrapper controlledPopupDisabled>{children}</PropWrapper>;
        case 'popupDisabled':
            return <PropWrapper popupDisabled>{children}</PropWrapper>;
        case 'readOnlyInput':
            return <PropWrapper readOnlyInput>{children}</PropWrapper>;
        case 'readOnlyDropdown':
            return <PropWrapper readOnlyDropdown>{children}</PropWrapper>;
         case 'readOnlyDropdownOption':
            return <PropWrapper readOnlyDropdownOption>{children}</PropWrapper>;
        case 'readOnlyNestedDropdown':
            return <PropWrapper readOnlyNestedDropdown>{children}</PropWrapper>;
        case 'onHoverOnClick':
            return <PropWrapper onHoverOnClick>{children}</PropWrapper>;
        case 'link':
            return <PropWrapper link>{children}</PropWrapper>;
        case 'hidden':
            return '';
        case 'restricted':
            return fourZeroThree;
        default:    
            return '';
    }
}

const checkProductExisit = (value) => {
    return value === undefined || (typeof value === 'object' && value !== null) || value === true;
}

export const multipleRestriction = (permissions = [], userPermissions = {}) => {
    let perm = [];
    permissions.forEach(permission => {
        perm.push(StringUtils.getNestedKey(userPermissions, permission)?.access_control)
    });
    return perm.every(p => p === "off" || !p);
}

export const productRestriction = (productPermission = '', productPermissions = {}) => {
    const routes = checkProductExisit(StringUtils.getNestedKey(productPermissions?.routes, productPermission));
    const unique = checkProductExisit(StringUtils.getNestedKey(productPermissions?.unique, productPermission));
    if(!routes){
        //product permissions restricted routes
        return routes;
    }
    if(!unique){
        //product permissions restricted for unique locations
        return unique;
    }
    if(routes && unique){
        return true
    }
}

export const RestrictView = ({type, children, error403, isRestricted}) => {
    return isRestricted ? <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper> : children;
}

const RestrictedView = ({ 
    children, 
    type,
    userPermission = false,
    productPermission = '',
    userRestrict = false,
    assignmentIds,
    modifiers = false, 
    profileUserId,
    isContact,
    error403,
    accessRole,
}) => { 
    const { 
        userPermissions, 
        productPermissions, 
        user_id, 
        userTeams,
        contactCustomerStatus,
        accountCustomerStatus,
        user_product_limits, 
        sequence_product_limits, 
        custom_fields_group_product_limits,
        automation_product_limits,
        lead_provider_limits,
        external_number_product_limits,
        product_catalog_product_limits,
        records_product_limits,
    } = useSelector(state => ({
        productPermissions: state.subscription.productPermissions,
        userPermissions: state.role.userPermissions.permission,
        user_id: state.auth.user_id,
        userTeams: state.teams.userTeams,
        accountCustomerStatus: state.account.account?.customer,
        contactCustomerStatus: state.contact?.account,
        user_product_limits: state.user.user_product_limits,
        sequence_product_limits: state.sequences.sequence_product_limits,
        custom_fields_group_product_limits: state.custom_fields_group.custom_fields_group_product_limits,
        automation_product_limits: state.automation.automation_product_limits,
        lead_provider_limits: state.lead_ingestion.lead_provider_limits,
        external_number_product_limits: state.pbx_config.external_number_product_limits,
        product_catalog_product_limits: state.products.product_catalog_product_limits,
        records_product_limits: state.contact.records_product_limits,
    }));
    let routePermission = StringUtils.getNestedKey(productPermissions?.routes, productPermission);
    let uniquePermission = StringUtils.getNestedKey(productPermissions?.unique, productPermission);

    if(!(checkProductExisit(routePermission))){
        //product permissions route off
        return <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
    } 
    if((checkProductExisit(routePermission) && Object.keys(routePermission ?? {}).length > 0)){
        //product permissions routes restricted
        switch(productPermission){ 
            case PRODUCT_PERMISSIONS.SETTINGS_TEXT_TEMPLATES:
                return (user_product_limits?.template < routePermission?.text_template_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_EMAIL_TEMPLATES:
                return (user_product_limits?.template < routePermission?.email_template_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_PERMISSION_PROFILE:
                return (user_product_limits?.custom_role < routePermission?.permission_set_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_SEQUENCES:
                return (sequence_product_limits?.sequence < routePermission?.sequence_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_DATA_CUSTOMIZATION:
                return (custom_fields_group_product_limits?.custom_groups < routePermission?.custom_group_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_TEAMS:
                return (user_product_limits?.teams < routePermission?.team_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_ASSIGNMENT_RULES:
                return (automation_product_limits?.assignment_rules < routePermission?.rule_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
             case PRODUCT_PERMISSIONS.SETTINGS_DATA_PROVIDERS:
                return (lead_provider_limits?.providers < routePermission?.provider_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
            case PRODUCT_PERMISSIONS.SETTINGS_PHONE_MANAGEMENT + PRODUCT_PERMISSIONS.SETTINGS_EXTERNAL_NUMBERS:
                return (external_number_product_limits?.external_numbers < routePermission?.external_number_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>; 
            case PRODUCT_PERMISSIONS.SETTINGS_PRODUCT_CATALOG:
                if(USER_PERMISSIONS.PRODUCT_CATALOG_PRODUCTS_ADD === modifiers){
                    return (product_catalog_product_limits?.product < routePermission?.product_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>; 
                }
                if(USER_PERMISSIONS.PRODUCT_CATALOG_CATEGORY_ADD === modifiers){
                    return (product_catalog_product_limits?.product_category < routePermission?.category_add) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>; 
                }
            }
    }
    if(Object.hasOwn(productPermissions?.unique ?? "", productPermission) || PRODUCT_PERMISSIONS.SETTINGS_USERS === productPermission){
        //product permissions unique restricted
        switch(productPermission){
            case PRODUCT_PERMISSIONS.SETTINGS_USERS:
                const roles = productPermissions?.unique;
                const matchingRole = user_product_limits?.roles?.find(role => role.access_role_id === accessRole && Object.hasOwn(roles ?? {}, role?.access_role_name));
                if (!matchingRole) {
                    return children;
                }
                return (matchingRole.access_role_count < roles?.[matchingRole?.access_role_name]) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
             case PRODUCT_PERMISSIONS.RECORDS:
                return (records_product_limits?.records < uniquePermission) ? children : <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>; 
            }
    }
    if(!(checkProductExisit(uniquePermission))){
        //product permissions unique off
        return <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
    }                          
    if(!userPermission){ return children }; //exit only if product permission
    if(!(userPermissions?.required_access)) {
        //userPermission not avalible, required_acccess is required, hide elements
        if(type === 'disabled' || type === 'hidden' || type === 'readOnly'){
            return <AccessWrapper type="hidden" fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper>;
        }
        return fourZeroThree();
    }
    if(userPermissions?.required_access){
        let userExisits;
        let restrictedAtrributes = [];
        const permission = StringUtils.getNestedKey(userPermissions, userPermission);
        const accessControl = permission?.access_control;
        const attributes = permission?.attribute;
        if(Array.isArray(assignmentIds)){
            // assignment right panel & ownedby and co-ownedby
            if(attributes?.includes("unassigned")){
                assignmentIds.forEach(assignmentId => restrictedAtrributes.push(assignmentId === 0));
            }
            if(attributes?.includes("assigned_owner_coowner") || attributes?.includes("user_only")){
                assignmentIds.forEach(assignmentId => restrictedAtrributes.push(assignmentId === user_id));
            }
            if(attributes?.includes("assigned_to_team") || attributes?.includes("my_teams") && userTeams.length > 0){
                assignmentIds.forEach(assignmentId => restrictedAtrributes.push(userTeams.includes(assignmentId)));
            }
            userExisits = restrictedAtrributes.some(attribute => attribute === true);
        } else if(modifiers){
            //specific strings used to restrict access
            if(modifiers === "personal"){
                restrictedAtrributes.push(attributes?.includes("personal"));
            }
            if(modifiers === "company"){
                restrictedAtrributes.push(attributes?.includes("company"));
            }
            userExisits = restrictedAtrributes.some(attribute => attribute === true);
        } else if (!Array.isArray(assignmentIds) && !profileUserId && (accountCustomerStatus || contactCustomerStatus)){
            //orders
            if(attributes?.includes("new")){
                if(isContact){
                    restrictedAtrributes.push(contactCustomerStatus?.customer_status === "active" && contactCustomerStatus?.fund_type === "unfunded" || contactCustomerStatus?.customer_status === "cancelled" && contactCustomerStatus?.fund_type === "funded");
                } else {
                    restrictedAtrributes.push(accountCustomerStatus?.customer_status === "active" && accountCustomerStatus?.fund_type === "unfunded" || accountCustomerStatus?.customer_status === "cancelled" && accountCustomerStatus?.fund_type === "funded");
                }
            }
            if(attributes?.includes("upsell")){
                if(isContact){
                    restrictedAtrributes.push(contactCustomerStatus?.customer_status !== "cancelled" && contactCustomerStatus?.fund_type === "funded");
                } else {
                    restrictedAtrributes.push(accountCustomerStatus?.customer_status !== "cancelled" && accountCustomerStatus?.fund_type === "funded");
                }
            }
            userExisits = restrictedAtrributes.some(attribute => attribute === true);
        } else {
            // user profile
            const isOwnProfile = user_id === profileUserId;
            const isMyTeamProfile =
                userTeams?.length > 0 &&
                userTeams.includes(profileUserId);
            userExisits =
                (attributes?.includes("user_only") && isOwnProfile) ||
                (attributes?.includes("my_teams") && (isOwnProfile || isMyTeamProfile));
        }
        const access = accessControl === "global" || (accessControl === "restricted" && !userRestrict) ? children
                : (accessControl === "restricted" && userRestrict) ? !userExisits ? <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper> : children
                    : accessControl === "off" ? <AccessWrapper type={type} fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper> : <AccessWrapper type="hidden" fourZeroThree={fourZeroThree(error403)}>{children}</AccessWrapper> 
        return access;
    } 
    return fourZeroThree(); //fallback if no permission found.
}

export default RestrictedView;

 	